import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy, WritableSignal, signal } from '@angular/core';
import { MedicalHistory, MedicalHistoryData } from '@models/medical-history.model';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AlertService } from './alert.service';

@Injectable({
  providedIn: 'root',
})
// medical history is an internal userfield as well as a requested clinical event.
export class MedicalHistoryService implements OnDestroy {
  $medicalHistory: WritableSignal<MedicalHistory> = signal(new MedicalHistory({} as MedicalHistoryData));
  $updateProfile: WritableSignal<boolean> = signal(false);
  $userId: WritableSignal<number> = signal(null);
  private readonly unsubscribe$ = new Subject<void>();
  constructor(
    private readonly alert: AlertService,
    private readonly http: HttpClient,
    private readonly translateService: TranslateService,
  ) {}

  private getBaseUrl(userId: number): string {
    return `/api/users/${userId}/medical-history`;
  }

  setMedicalHistoryStages(currentMedicalHistory: MedicalHistory, newMedicalHistory: MedicalHistory) {
    currentMedicalHistory.generalHealth = newMedicalHistory.generalHealth;
    currentMedicalHistory.familyHistory = newMedicalHistory.familyHistory;
    currentMedicalHistory.chronicItems = newMedicalHistory.chronicItems;
    currentMedicalHistory.allergies = newMedicalHistory.allergies;
    currentMedicalHistory.operationHistory = newMedicalHistory.operationHistory;
    currentMedicalHistory.treatmentHistory = newMedicalHistory.treatmentHistory;
    currentMedicalHistory.occupationalAndDemographicalHistory = newMedicalHistory.occupationalAndDemographicalHistory;
    currentMedicalHistory.vaccinations = newMedicalHistory.vaccinations;
    currentMedicalHistory.travelHistory = newMedicalHistory.travelHistory;
    currentMedicalHistory.gynaecologicalHistory = newMedicalHistory.gynaecologicalHistory;
    return currentMedicalHistory;
  }

  // Get from profile
  getSavedMedicalHistory$(userId: number): Observable<MedicalHistory> {
    return this.http.get<MedicalHistoryData>(`${this.getBaseUrl(userId || this.$userId())}`).pipe(
      catchError((error: unknown) => {
        this.alert.handleErrorDialog$(error, this.translateService.instant('error_fetching_medical_history'));
        throw error;
      }),
      map((requestData) => new MedicalHistory(requestData)),
    );
  }

  // Save to profile
  saveMedicalHistory$(userId: number, section: string, params: any): Observable<MedicalHistory> {
    return this.http.patch<MedicalHistoryData>(`${this.getBaseUrl(userId)}`, { params, section }).pipe(
      catchError((error: unknown) => {
        this.alert.handleErrorDialog$(error, this.translateService.instant('error_saving_medical_history_step'));
        throw error;
      }),
      map((requestData) => new MedicalHistory(requestData)),
    );
  }

  // Get a medical history wfe request
  getMedicalHistory$(uuid: string): Observable<MedicalHistory> {
    return this.http.get<MedicalHistory>(`api/medical-history/request/${uuid}`).pipe(
      catchError((error: unknown) => {
        this.alert.handleErrorDialog$(error, this.translateService.instant('error_fetching_medical_history'));
        throw error;
      }),
      map((requestData) => new MedicalHistory(requestData)),
    );
  }

  // get medical history wfe requests
  getMedicalHistories$(userId: number): Observable<MedicalHistory[]> {
    return this.http.get<MedicalHistory[]>(`${this.getBaseUrl(userId)}/requests`).pipe(
      catchError((error: unknown) => {
        this.alert.handleErrorDialog$(error, this.translateService.instant('error_fetching_medical_histories'));
        throw error;
      }),
      map((requestData) => requestData.map((data) => new MedicalHistory(data))),
    );
  }

  // Save to wfe request
  saveRequestMedicalHistory$(requestUuid: string, section: string, params: MedicalHistoryData): Observable<MedicalHistoryData> {
    return this.http
      .patch<MedicalHistory>(`/api/medical-history/request/${requestUuid}`, {
        params,
        section,
        userID: this.$userId(),
      })
      .pipe(
        catchError((error: unknown) => {
          this.alert.handleErrorDialog$(error, this.translateService.instant('error_saving_medical_history_step'));
          throw error;
        }),
        map((requestData) => new MedicalHistory(requestData)),
      );
  }

  // submit request to wfe
  submitRequestMedicalHistory$(requestUuid: string, params: MedicalHistoryData): Observable<MedicalHistory> {
    return this.http.patch<MedicalHistory>(`/api/medical-history/request/${requestUuid}/submit`, { params }).pipe(
      catchError((error: unknown) => {
        this.alert.handleErrorDialog$(error, this.translateService.instant('error_submitting_medical_history_request'));
        throw error;
      }),
      map((requestData) => new MedicalHistory(requestData)),
    );
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
